import { Alert, AlertTitle, Box, Stack, Typography } from "@mui/material";
import { Link } from "react-router-dom";
import { useMatchProblemSummary } from "../../api/queries";
import { FIELD_LABELS } from "../../components/RuleEditor/constants";

/**
 * Rules that hit a dispatch they couldn't make, on the inbox.
 *
 * A `match` action has no default arm on purpose — the author is saying "these
 * are all the values this can be". A transaction none of the arms name means
 * that claim was wrong, which is a bug in the rule and not in the transaction.
 * The transaction page says so one transaction at a time; this is the only
 * place that says *which rule*, and how much of the ledger it is missing.
 *
 * Grouped by rule rather than by value: one rule with three unhandled sources
 * is one thing to go and edit.
 */
export const MatchProblemWarnings = () => {
  const { data } = useMatchProblemSummary();

  if (!data || data.counts.length === 0) return null;

  // Preserves the server's most-hit-first order within each rule, since the
  // first insertion fixes the rule's position.
  const byRule = new Map<string, typeof data.counts>();
  for (const count of data.counts) {
    const key = count.rule?.id ?? "";
    byRule.set(key, [...(byRule.get(key) ?? []), count]);
  }

  return (
    <Alert severity="warning" sx={{ mb: 2 }}>
      <AlertTitle>
        {data.transactionCount === 1
          ? "1 transaction a rule couldn't dispatch"
          : `${data.transactionCount} transactions a rule couldn't dispatch`}
      </AlertTitle>
      <Typography variant="body2" sx={{ mb: 0.5, color: "text.secondary" }}>
        A <strong>match</strong> action has no default arm, so a value none of
        its arms name is left alone and reported here. Add an arm for the values
        below, or the transactions keep arriving uncategorized.
      </Typography>
      <Stack spacing={0.5} sx={{ mt: 0.5 }}>
        {[...byRule.entries()].map(([ruleId, counts]) => (
          <Box key={ruleId}>
            <Typography
              component="span"
              variant="body2"
              sx={{ fontWeight: 600 }}
            >
              {counts[0].rule ? (
                <Link to={`/admin/auto-transactions/${counts[0].rule.id}`}>
                  {counts[0].rule.name}
                </Link>
              ) : (
                // Applied to the transaction directly rather than through a
                // rule — there is no rule page to send anyone to.
                "A one-off edit"
              )}
            </Typography>
            <Typography
              component="span"
              variant="body2"
              sx={{ color: "text.secondary", ml: 1 }}
            >
              {counts.map((count, index) => (
                <Box component="span" key={`${count.field}-${count.value}`}>
                  {index > 0 && ", "}
                  no arm for {FIELD_LABELS[count.field].toLowerCase()}{" "}
                  <strong>“{count.value}”</strong> ({count.transactionCount})
                </Box>
              ))}
            </Typography>
          </Box>
        ))}
      </Stack>
      <Typography variant="body2" sx={{ mt: 0.5 }}>
        <Link to="/transactions?matchProblem=true">
          Show the affected transactions
        </Link>
      </Typography>
    </Alert>
  );
};
